import {useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {ChatService} from 'services/Chat/ChatService';
import {Chat, ChatMessage, CurrentUser} from 'services/Chat/types';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Client} from '@stomp/stompjs';
import {AuthService} from '@services/AuthService';
import {PaginatedResponse} from 'types/pagination';
import {useStompChat} from './StompChatClient/useStompChat';

type ChatContextType = {
    currentChatMessages: {
        read: ChatMessage[] | undefined;
        unread: ChatMessage[] | undefined;
    };
    chats: Chat[];
    selectedChat: Chat | null;
    setSelectedChat: (chat: Chat | null) => void;
    handleSelectChat: (chat: Chat) => void;
    currentUser?: CurrentUser;
    chatUsers?: string[];
    getPrivateChat: (userName: string) => void;
    sendMessage: (payload: {content: string; type: string; dateTime: string}) => void;
    stompClient: Client | null;
    setStompClient: (client: Client | null) => void;
    getGroupChat: (userIds: string[], title: string) => void;
    chatInfo: Chat | null;
    editGroupChat: (chatId: string, title: string, userIds: string[]) => void;
    readMessages: (ids: number[]) => void;
    readAllMessages: () => void;
    loadMoreHistory: () => void;
    hasMoreHistory: boolean;
    isLoadingMoreHistory: boolean;
    totalUnreadCount: number;
    updateUnreadCount: (update: {id: string; unreadMessages: number | undefined}) => void;
};

const ChatContext = createContext<ChatContextType | null>(null);

export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useMessengerContext must be used within a MessengerContextProvider');
    }
    return context;
};

export const MessengerContextProvider = ({children}: {children: React.ReactNode}) => {
    const queryClient = useQueryClient();

    const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
    const [stompClient, setStompClient] = useState<Client | null>(null);

    const {data: chats = []} = useQuery<Chat[]>({
        queryKey: ['chats'],
        queryFn: () => ChatService.getChats(),
    });

    const {data: chatUsers} = useQuery({
        queryKey: ['chatUsers'],
        queryFn: () => ChatService.getChatUsers(),
    });

    const {
        data: readPages,
        fetchPreviousPage, // для загрузки page+1
        hasPreviousPage: hasMoreHistory,
        isFetchingPreviousPage: isLoadingMoreHistory,
    } = useInfiniteQuery<PaginatedResponse<ChatMessage[]>>({
        queryKey: ['chatHistory', selectedChat?.id],
        queryFn: ({pageParam = 0}) =>
            ChatService.getChatReadMessages(selectedChat!.id, {page: pageParam, size: 100}),
        getPreviousPageParam: (first) =>
            first.pageable.pageNumber + 1 < first.totalPages
                ? first.pageable.pageNumber + 1
                : undefined,
        enabled: !!selectedChat?.id,
        initialPageParam: 0,
        getNextPageParam: (last) =>
            last.pageable.pageNumber + 1 < last.totalPages
                ? last.pageable.pageNumber + 1
                : undefined,
    });

    const {data: unreadPage} = useQuery<PaginatedResponse<ChatMessage[]>>({
        queryKey: ['chatUnread', selectedChat?.id],
        queryFn: () => ChatService.getChatUnreadMessages(selectedChat!.id, {page: 0, size: 100}),
        enabled: !!selectedChat?.id,
    });

    const readList = useMemo(() => {
        const all = readPages?.pages.flatMap((p) => p.content) ?? [];
        return all.sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
    }, [readPages]);

    const unreadList = unreadPage?.content ?? [];

    const {data: currentUser} = useQuery({
        queryKey: ['currentUser'],
        queryFn: () => ChatService.getCurrentUser(),
    });

    const {data: chatInfo} = useQuery({
        queryKey: ['chatInfo', selectedChat?.id],
        queryFn: () => ChatService.getChatById(selectedChat!.id),
        enabled: !!selectedChat?.id,
    });

    const sendMessage = (payload: {content: string; type: string; dateTime: string}) => {
        if (!stompClient?.connected) {
            return;
        }
        if (selectedChat) {
            const token = AuthService.getToken();
            stompClient.publish({
                destination: `/app/chat/${selectedChat.id}/send`,
                body: JSON.stringify(payload),
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        }
    };

    const readMessages = (messageIds: number[]) => {
        if (stompClient && selectedChat && messageIds.length) {
            stompClient.publish({
                destination: `/app/chat/${selectedChat.id}/message/read`,
                body: JSON.stringify({messageIds}),
            });
        }
    };

    const readAllMessages = () => {
        if (stompClient && selectedChat) {
            stompClient.publish({
                destination: `/app/chat/${selectedChat.id}/message/read/all`,
            });
        }
    };

    const handleSelectChat = (chat: Chat) => {
        setSelectedChat(chat);
    };

    const getOrCreatePrivateChat = useMutation({
        mutationFn: (userName: string) => ChatService.getPrivateChat(userName),
        onSuccess: (newChat) => {
            queryClient.invalidateQueries({queryKey: ['chats']});
            setSelectedChat(newChat);
        },
    });

    const getPrivateChat = (userName: string) => {
        getOrCreatePrivateChat.mutate(userName);
    };

    const createGroupChatMutation = useMutation({
        mutationFn: (vars: {userIds: string[]; title: string}) =>
            ChatService.createGroupChat(vars.userIds, vars.title),
        onSuccess: (newChat) => {
            queryClient.invalidateQueries({queryKey: ['chats']});
            queryClient.invalidateQueries({queryKey: ['chatInfo', newChat.id]});
            setSelectedChat(newChat);
        },
    });

    const getGroupChat = (userIds: string[], title: string) => {
        createGroupChatMutation.mutate({userIds, title});
    };

    const editGroupChatMutation = useMutation({
        mutationFn: (vars: {chatId: string; title: string; userIds: string[]}) =>
            ChatService.editGroupChat(vars.chatId, vars.title, vars.userIds),
        onSuccess: (newChat) => {
            queryClient.invalidateQueries({queryKey: ['chats']});
            queryClient.invalidateQueries({queryKey: ['chatInfo', newChat.id]});
            setSelectedChat(newChat);
        },
    });

    const editGroupChat = (chatId: string, title: string, userIds: string[]) => {
        editGroupChatMutation.mutate({chatId, title, userIds});
    };

    const updateUnreadCount = useCallback(
        (update: {id: string; unreadMessages: number | undefined}) => {
            queryClient.setQueryData<Chat[]>(['chats'], (old = []) =>
                old.map((c) =>
                    c.id === update.id ? {...c, unreadMessages: update.unreadMessages} : c,
                ),
            );
        },
        [queryClient],
    );

    const totalUnreadCount = useMemo(() => {
        return chats.reduce((acc, chat) => acc + (chat.unreadMessages ?? 0), 0);
    }, [chats]);

    const value: ChatContextType = {
        currentChatMessages: {read: readList, unread: unreadList},
        loadMoreHistory: fetchPreviousPage,
        hasMoreHistory,
        isLoadingMoreHistory,
        chats: chats ?? [],
        selectedChat,
        setSelectedChat,
        handleSelectChat,
        currentUser,
        chatUsers,
        getPrivateChat,
        sendMessage,
        stompClient,
        setStompClient,
        getGroupChat,
        chatInfo: chatInfo ?? null,
        editGroupChat,
        readMessages,
        readAllMessages,
        totalUnreadCount,
        updateUnreadCount,
    };

    return (
        <ChatContext.Provider value={value}>
            <StompInitializer />
            {children}
        </ChatContext.Provider>
    );
};

const StompInitializer = () => {
    useStompChat();
    return null;
};
